import { Citation, DataSource } from '../models/elsie-ai.model';

/**
 * Maps citation ordinals to their corresponding Citation objects and returns them in order.
 *
 * Given a Map of citation ordinals (responseId -> ordinal) and an array of Citation objects,
 * this function finds each Citation by its responseId, attaches its ordinal as citationIndex,
 * and returns an array of Citations sorted by citationIndex ascending.
 * Useful for displaying citations in the order they appear in the AI response.
 */
export const getCitationsFromOrdinals = ({
  citationOrdinals,
  citations,
}: {
  citationOrdinals: Map<string, number>;
  citations: Citation[];
}): Citation[] => {
  const result: Citation[] = [];

  citationOrdinals.forEach((ordinal, responseId) => {
    const citation = citations.find((c) => String(c.id) === String(responseId));

    if (citation) {
      result.push({
        ...citation,
        citationIndex: ordinal,
      });
    }
  });

  return result.sort((a, b) => (a.citationIndex ?? 0) - (b.citationIndex ?? 0));
};

/**
 * Normalizes citation text by converting [[number]] format to [number]() format
 * for proper markdown rendering in the chat interface.
 */
export const normalizeCitations = (text: string): string => {
  return text.replace(/\[\[(\d+)\]\]/g, '[$1]()');
};

/**
 * Creates a citation ordinals map from the API response format
 */
export const createCitationOrdinalsMap = (
  citationOrdinals?: Record<string, number>
): Map<string, number> => {
  return new Map(Object.entries(citationOrdinals || {}));
};

/**
 * Extracts distinct data sources from citations array
 */
export const extractDistinctDataSources = (citations: Citation[]): DataSource[] => {
  return Array.from(
    new Set(
      citations.map((c) => c.dataSource).filter((ds): ds is DataSource => ds && ds.trim() !== '')
    )
  );
};
